import { Overlay, OverlayOutsideClickDispatcher, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { ElementRef, Injectable, InjectionToken, Injector, inject } from '@angular/core';
import { AppStateService } from '@siza-staff-care/shared/app-state';
import { Observable, Subject, filter, share, takeUntil, tap } from 'rxjs';

export class PanelRef {
	//SCS-345: SUPER URGENT - Data Error when applying rota options
	public disableClose = true;

	private afterClosedSubject = new Subject<unknown>();

	constructor(private overlayRef: OverlayRef) {}

	/**
	 * Closes the overlay, optionally providing a result.
	 */
	public close(result?: unknown) {
		this.overlayRef.dispose();
		this.afterClosedSubject.next(result);
		this.afterClosedSubject.complete();
	}

	/**
	 * An Observable that notifies when the overlay has closed
	 */
	public afterClosed(): Observable<unknown> {
		return this.afterClosedSubject.asObservable();
	}
}

export const PANEL_DATA = new InjectionToken<unknown>('PANEL_DATA');
export const PANEL_CONFIG = new InjectionToken<unknown>('PANEL_CONFIG');
export const ORIGIN_REF = new InjectionToken<unknown>('ORIGIN_REF');

export type PanelConfig = {
	data?: unknown;
	offsetX?: number;
	offsetY?: number;
	viewportMargin?: number;
	hasBackdrop?: boolean;
	backdropClass?: string;
	opaqueBackdrop?: boolean;
	injector?: Injector;
	emitCloseOnClickOutside?: boolean;
	clickOutsideToClose?: boolean;
	originX?: 'start' | 'center' | 'end';
	originY?: 'top' | 'center' | 'bottom';
	overlayX?: 'start' | 'center' | 'end';
	overlayY?: 'top' | 'center' | 'bottom';
};

@Injectable({
	providedIn: 'root',
})
export class PopupPanelService {
	private appState = inject(AppStateService);
	private overlay = inject(Overlay);
	private injector = inject(Injector);
	private outsideClickDispatcher = inject(OverlayOutsideClickDispatcher);

	show<T>(originRef: ElementRef | undefined, componentType: ComponentType<T>, config?: PanelConfig) {
		const positionStrategy = originRef
			? this.overlay
					.position()
					.flexibleConnectedTo(originRef)
					.withDefaultOffsetX(config?.offsetX ?? 0)
					.withDefaultOffsetY(config?.offsetY ?? 0)
					.withViewportMargin(config?.viewportMargin ?? 2)
					// Configure positions.
					// Try to connect to the element's right edges first.
					// Then fall back to connecting to the left edges.
					.withPositions([
						{
							originX: config?.originX ?? 'end',
							originY: config?.originY ?? 'bottom',
							overlayX: config?.overlayX ?? 'end',
							overlayY: config?.overlayY ?? 'top',
						},
						{
							originX: config?.originX ?? 'start',
							originY: config?.originY ?? 'bottom',
							overlayX: config?.overlayX ?? 'start',
							overlayY: config?.overlayY ?? 'top',
						},
					])
			: this.overlay.position().global().centerHorizontally().centerVertically();

		const backdropClass = config?.opaqueBackdrop
			? 'overlay-backdrop'
			: config?.backdropClass || 'cdk-overlay-transparent-backdrop';

		const overlayRef = this.overlay.create({
			positionStrategy,
			hasBackdrop: config?.hasBackdrop ?? true,
			backdropClass,
		});

		const panelRef = new PanelRef(overlayRef);
		const afterClosed = panelRef.afterClosed().pipe(share());

		// Close the panel if no longer authenticated.
		this.appState.isAuthenticated$
			.pipe(
				takeUntil(afterClosed),
				filter((isAuthenticated) => !isAuthenticated),
				tap(() => {
					panelRef.close();
				}),
			)
			.subscribe();

		overlayRef
			.outsidePointerEvents()
			.pipe(
				tap((e) => {
					// Only close on backdrop click of our backdrop element.
					// Fixes the issue with other overlays, e.g. mat-select.
					if (e.target !== overlayRef.backdropElement) {
						return;
					}
					
					//SCS-345: SUPER URGENT - Data Error when applying rota options
					if (config?.emitCloseOnClickOutside && !this.appState.isOptionStoreApplyingStaffChanges) {
                        panelRef.close();
                        overlayRef.dispose();
                    }

/*					if (config?.emitCloseOnClickOutside) {
						panelRef.close();
					}
					overlayRef.dispose();
*/				}),
				takeUntil(afterClosed),
			)
			.subscribe();

		const injector = Injector.create({
			parent: config?.injector ?? this.injector,
			providers: [
				{ provide: PanelRef, useValue: panelRef },
				{ provide: PANEL_DATA, useValue: config?.data },
				{ provide: PANEL_CONFIG, useValue: config },
				{ provide: ORIGIN_REF, useValue: originRef },
			],
		});

		// Attach component portal to the overlay
		const portal = new ComponentPortal(componentType, null, injector);
		overlayRef.attach(portal);

		this.outsideClickDispatcher.add(overlayRef);

		return afterClosed;
	}
}
